import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, UserMinus, UserPlus } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { teamApi, type TeamMember } from "@/features/settings/api";
import { useAuthStore } from "@/features/auth/store";
import { cn } from "@/lib/utils";

const KEY = ["team"] as const;

const ROLE_OPTIONS = [
  { value: "admin", label: "Admin" },
  { value: "manager", label: "Manager" },
  { value: "member", label: "Member" },
];

const ROLE_STYLES: Record<string, string> = {
  owner: "text-purple-600 bg-purple-50 dark:bg-purple-950/40 dark:text-purple-400",
  admin: "text-rose-600 bg-rose-50 dark:bg-rose-950/40 dark:text-rose-400",
  manager: "text-amber-600 bg-amber-50 dark:bg-amber-950/40 dark:text-amber-400",
  member: "text-muted-foreground bg-muted",
};

export default function TeamTab() {
  const me = useAuthStore((s) => s.user);
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: [...KEY, "list"],
    queryFn: teamApi.list,
    staleTime: 30_000,
  });
  const members = q.data ?? [];

  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<TeamMember | null>(null);

  function invalidate() {
    qc.invalidateQueries({ queryKey: [...KEY, "list"] });
  }

  const deactivate = useMutation({
    mutationFn: (id: string) => teamApi.deactivate(id),
    onSuccess: () => {
      toast.success("Member deactivated");
      invalidate();
    },
    onError: (err) => {
      const e = err as { response?: { data?: { error?: string } } };
      toast.error(e?.response?.data?.error || "Failed to deactivate");
    },
  });

  const reactivate = useMutation({
    mutationFn: (id: string) => teamApi.reactivate(id),
    onSuccess: () => {
      toast.success("Member reactivated");
      invalidate();
    },
    onError: () => toast.error("Failed to reactivate"),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold">Team Members</h2>
          <p className="text-sm text-muted-foreground">
            Invite team members and manage their roles.
          </p>
        </div>
        <Button onClick={() => setAddOpen(true)}>
          <Plus className="size-4" />
          Invite Member
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {q.isLoading && (
            <div className="space-y-2 p-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          )}
          {!q.isLoading && members.length === 0 && (
            <p className="px-6 py-10 text-center text-sm text-muted-foreground">
              No team members yet.
            </p>
          )}
          {!q.isLoading && members.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3 font-medium">Name</th>
                    <th className="px-4 py-3 font-medium">Email</th>
                    <th className="px-4 py-3 font-medium">Role</th>
                    <th className="px-4 py-3 font-medium">Last login</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 text-right font-medium">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {members.map((m) => {
                    const isSelf = me?.id === m.id;
                    return (
                      <tr key={m.id}>
                        <td className="px-4 py-2.5">
                          {m.full_name ||
                            `${m.first_name} ${m.last_name}`.trim() ||
                            "—"}
                        </td>
                        <td className="px-4 py-2.5 text-muted-foreground">
                          {m.email}
                        </td>
                        <td className="px-4 py-2.5">
                          <span
                            className={cn(
                              "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize",
                              ROLE_STYLES[m.role] ??
                                "text-muted-foreground bg-muted",
                            )}
                          >
                            {m.role}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-muted-foreground">
                          {m.last_login_at
                            ? new Date(m.last_login_at).toLocaleString()
                            : "—"}
                        </td>
                        <td className="px-4 py-2.5">
                          <span
                            className={cn(
                              "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
                              m.is_active
                                ? "text-emerald-600 bg-emerald-50 dark:bg-emerald-950/40 dark:text-emerald-400"
                                : "text-muted-foreground bg-muted",
                            )}
                          >
                            {m.is_active ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="size-8"
                              onClick={() => setEditTarget(m)}
                              aria-label="Edit"
                            >
                              <Pencil className="size-4" />
                            </Button>
                            {m.is_active ? (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="size-8 text-destructive hover:text-destructive"
                                onClick={() => deactivate.mutate(m.id)}
                                disabled={isSelf || deactivate.isPending}
                                aria-label="Deactivate"
                                title={isSelf ? "Can't deactivate yourself" : ""}
                              >
                                <UserMinus className="size-4" />
                              </Button>
                            ) : (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="size-8 text-emerald-600 hover:text-emerald-600 dark:text-emerald-400"
                                onClick={() => reactivate.mutate(m.id)}
                                disabled={reactivate.isPending}
                                aria-label="Reactivate"
                              >
                                <UserPlus className="size-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <AddMemberDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        onSaved={invalidate}
      />
      <EditMemberDialog
        target={editTarget}
        onClose={() => setEditTarget(null)}
        onSaved={invalidate}
      />
    </div>
  );
}

function AddMemberDialog({
  open,
  onOpenChange,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onSaved: () => void;
}) {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [role, setRole] = useState("member");
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setEmail("");
      setFirstName("");
      setLastName("");
      setRole("member");
      setError("");
    }
  }, [open]);

  const create = useMutation({
    mutationFn: () =>
      teamApi.create({
        email: email.trim(),
        first_name: firstName,
        last_name: lastName,
        role,
      }),
    onSuccess: () => {
      toast.success("Invitation sent");
      onSaved();
      onOpenChange(false);
    },
    onError: (err) => {
      const data = (err as { response?: { data?: Record<string, unknown> } })
        ?.response?.data;
      if (typeof data?.error === "object" && data.error) {
        setError(
          (data.error as { message?: string }).message || "Failed to add",
        );
      } else if (data && typeof data === "object") {
        const messages = Object.entries(data)
          .filter(([k]) => k !== "error")
          .map(
            ([k, v]) =>
              `${k}: ${Array.isArray(v) ? (v as string[]).join(", ") : v}`,
          )
          .join(" | ");
        setError(messages || "Failed to add");
      } else {
        setError("Failed to add team member.");
      }
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Invite Team Member</DialogTitle>
          <DialogDescription>
            They&apos;ll get an email with a link to set their own password and
            join your workspace.
          </DialogDescription>
        </DialogHeader>

        {error && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </div>
        )}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            setError("");
            if (!email) {
              setError("Email is required.");
              return;
            }
            create.mutate();
          }}
          className="space-y-3"
        >
          <div className="space-y-1.5">
            <Label htmlFor="add-email">Email</Label>
            <Input
              id="add-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="add-first">First name</Label>
              <Input
                id="add-first"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="add-last">Last name</Label>
              <Input
                id="add-last"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Role</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ROLE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? "Sending…" : "Send invite"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditMemberDialog({
  target,
  onClose,
  onSaved,
}: {
  target: TeamMember | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [role, setRole] = useState("member");
  const [error, setError] = useState("");

  useEffect(() => {
    if (target) {
      setFirstName(target.first_name || "");
      setLastName(target.last_name || "");
      setRole(target.role || "member");
      setError("");
    }
  }, [target]);

  const update = useMutation({
    mutationFn: () =>
      teamApi.update(target!.id, {
        first_name: firstName,
        last_name: lastName,
        role,
      }),
    onSuccess: () => {
      toast.success("Member updated");
      onSaved();
      onClose();
    },
    onError: (err) => {
      const data = (err as { response?: { data?: Record<string, unknown> } })
        ?.response?.data;
      if (data && typeof data === "object") {
        const messages = Object.entries(data)
          .map(
            ([k, v]) =>
              `${k}: ${Array.isArray(v) ? (v as string[]).join(", ") : v}`,
          )
          .join(" | ");
        setError(messages);
      } else {
        setError("Failed to update member.");
      }
    },
  });

  return (
    <Dialog open={!!target} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Member</DialogTitle>
        </DialogHeader>
        {error && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </div>
        )}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            update.mutate();
          }}
          className="space-y-3"
        >
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="edit-first">First name</Label>
              <Input
                id="edit-first"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-last">Last name</Label>
              <Input
                id="edit-last"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Role</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ROLE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={update.isPending}>
              {update.isPending ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
